import React, {useContext} from "react";
import "./Markers.scss";
import MarkerText from "./MarkerText";
import {PIN_OFFSETS} from "./markerUtils";
import {MapContext} from "../../../contexts/MapContext";

const calculatePolygon = (coordsList) => {
    let minX = Infinity,
        minY = Infinity,
        maxX = -Infinity,
        maxY = -Infinity;

    coordsList.forEach(([x, y]) => {
        minX = Math.min(minX, x);
        maxX = Math.max(maxX, x);
        minY = Math.min(minY, y);
        maxY = Math.max(maxY, y);
    });

    const polygonSize = {
        width: maxX - minX,
        height: maxY - minY,
    };

    const polygonBottomCenter = [(minX + maxX) / 2, maxY];

    return {polygonSize, polygonBottomCenter, minX, minY};
};

export default function GroupMarker({
                                        name,
                                        coords_of_polygon = [],
                                        coords_of_pins,
                                        onClick,
                                        align = "right",
                                    }) {
    const {zoom, isDragging} = useContext(MapContext); // Get zoom from context
    const inverseZoom = 1 / zoom; // Inverse scale for markers and text

    // Check for valid coordinates
    if (!coords_of_polygon || coords_of_polygon.length === 0) {
        return null;
    }

    const {polygonSize, polygonBottomCenter, minX, minY} = calculatePolygon(coords_of_polygon);

    const polygonPoints = coords_of_polygon
        ?.map((coord) => coord.join(","))
        .join(" ");

    const clipPathPoints = coords_of_polygon.map(([x, y]) => {
        const percentX = ((x - minX) / polygonSize.width) * 100;
        const percentY = ((y - minY) / polygonSize.height) * 100;
        return `${percentX}% ${percentY}%`;
    });

    return (
        <g
            className="marker-group"
            onClick={onClick}
            data-coords={polygonBottomCenter.join(",")}
            data-name={name}
        >
            {/* Polygon group - scales with map zoom */}
            {coords_of_polygon.length > 0 && (
                <g className="polygon-group">
                    <foreignObject
                        x={minX}
                        y={minY}
                        width={polygonSize.width}
                        height={polygonSize.height}
                    >
                        <div
                            className="hover-brighten"
                            style={{
                                width: "100%",
                                height: "100%",
                                clipPath: `polygon(${clipPathPoints.join(", ")})`,
                            }}
                        >
                            <svg width="100%" height="100%">
                                <polygon
                                    points={polygonPoints}
                                    strokeWidth={0}
                                    className="polygon"
                                />
                            </svg>
                        </div>
                    </foreignObject>
                </g>
            )}

            {/* Marker and text group - inversely scaled to maintain size */}
            <g
                className="marker-icon-group"
                style={{
                    transform: `scale(${inverseZoom})`,
                    transformOrigin: `${polygonBottomCenter[0]}px ${polygonBottomCenter[1]}px`,
                    transition: isDragging ? 'none' : 'transform 0.3s ease-out'
                }}
            >
                {coords_of_pins?.map((pin, index) => (
                    <svg
                        key={index}
                        x={pin[0] - 9}
                        y={pin[1] - 25}
                        className="image-pin"
                        width="18"
                        height="25"
                        viewBox="0 0 18 25"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M16.7254 12.6478C17.1833 11.5369 17.4375 10.3085 17.4375 9.0167C17.4375 4.03691 13.6599 0 9 0C4.3401 0 0.5625 4.03691 0.5625 9.0167C0.5625 10.0706 0.731703 11.0823 1.04266 12.0224L1.03125 12.022C1.06361 12.106 1.09657 12.1902 1.13011 12.2747C1.22534 12.5372 1.33174 12.7938 1.4487 13.0437C3.17275 17.0436 6.08638 21.4315 9 24.5452C12.1641 21.1639 15.5156 15.7539 16.7344 12.6481L16.7254 12.6478Z"
                            fill="black"
                        />
                        <ellipse cx="9" cy="8.13823" rx="4.5" ry="4.50835" fill="white"/>
                    </svg>
                ))}

                <MarkerText
                    name={name}
                    coords={polygonBottomCenter}
                    align={align}
                    pinSize={polygonSize}
                    marginToNextLine={PIN_OFFSETS.default.marginToNextLine}
                    offsets={PIN_OFFSETS.default}
                />
            </g>
        </g>
    );
}
